import { ofetch } from "ofetch";
import { Address, formatUnits, zeroAddress } from "viem";
import { chainlinkPriceFeedAbi } from "./abis/external";
import { formatDenom, parseDenom } from "./bigint";
import { catchBlock } from "./catchBlock";
import { EvmChainTag, getChain, getChainManager } from "./chains";


/**
 * Chainlink price feeds on Ethereum mainnet for various assets.
 * All return prices with 8 decimals.
 */
const PRICE_FEEDS: Partial<Record<string, { address: Address, chainTag: EvmChainTag }>> = {
  ETH:  { address: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419", chainTag: "ETHEREUM" },
  BNB:  { address: "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE", chainTag: "BSC" },
  AVAX: { address: "0x0A77230d17318075983913bC2145DB16C7366156", chainTag: "AVAX" },
  POL:  { address: "0x5E988c11a4f92155C30D9fb69Ed75597f712B113", chainTag: "BASE" },
  APE:  { address: "0xD10aBbC76679a20055E167BB80A24ac851b37056", chainTag: "ETHEREUM" },
  HYPE: { address: "0xa5a72eF19F82A579431186402425593a559ed352", chainTag: "HYPERLIQUID" },
  XPL:  { address: "0xF932477C37715aE6657Ab884414Bd9876FE3f750", chainTag: "PLASMA" },
  RON:  { address: "0x0B6074F21488B95945989E513EFEA070096d931D", chainTag: "RONIN" },
  MON:  { address: "0xBcD78f76005B7515837af6b50c7C52BCf73822fb", chainTag: "MONAD" },
  SEI:  { address: "0xCc9742d77622eE9abBF1Df03530594f9097bDcB3", chainTag: "ARBITRUM" },
  SOL:  { address: "0x4ffC43a60e009B551865A93d232E33Fce9f01507", chainTag: "ETHEREUM" },
  SUI:  { address: "0x491a921c41d6a97C57426E0c0108a231cd6E5f60", chainTag: "BASE" },
};


/**
 * Get the address of the USD price feed for a chain.
 * @param chainTag
 * @returns
 */
export const getUsdPriceFeed = (chainTag: EvmChainTag) => {
  const chain = getChain(chainTag);
  return PRICE_FEEDS[chain.currency.symbol];
};


/**
 * Normalize a price to a given format and decimals.
 * @param price
 * @param format
 * @param decimals
 * @returns
 */
function normalizePrice(price: number | bigint, format: "bigint", decimals: number): bigint;
function normalizePrice(price: number | bigint, format: "decimal", decimals: number): number;
function normalizePrice(
  price: number | bigint,
  format: "bigint" | "decimal",
  decimals: number,
): bigint | number {
  if (typeof price === "number" && !Number.isFinite(price)) throw new Error("Invalid price");
  if (format === "bigint" && typeof price === "bigint") return price;
  if (format === "decimal" && typeof price === "number") return price;
  if (format === "bigint" && typeof price === "number") return BigInt(Math.round(price * 10 ** decimals));
  if (format === "decimal" && typeof price === "bigint") return parseFloat(formatUnits(price, decimals));
  throw new Error("Invalid price format");
}



export const getOnChainUsdPrice = async (symbol: string) => {

  const priceFeed = PRICE_FEEDS[symbol];
  if (!priceFeed) return 0n;

  const evm = getChainManager(priceFeed.chainTag);
  const data = await evm.publicClient.readContract({
    address: priceFeed.address,
    abi: chainlinkPriceFeedAbi,
    functionName: "latestRoundData",
  });
  return BigInt(data[1]);
};


/**
 * Get the USD price of a token from Relay Link.
 * @param symbol
 * @param address The address of the token to get the price for.
 * @returns The price as a number (float, e.g. 3200.50).
 */
export const getRelayUsdPrice = async (symbol: string, address = zeroAddress) => {
  const priceFeed = PRICE_FEEDS[symbol];
  if (!priceFeed) return 0;
  const chain = getChain(priceFeed.chainTag);

  const url = `https://api.relay.link/currencies/token/price`;
  const { price } = await ofetch<{ price: number }>(url, {
    timeout: 2_000,
    query: { chainId: chain.id, address },
  });
  return price;
};


/**
 * Get the USD price of a token from CryptoCompare.
 * @param symbol
 * @returns
 */
export const getBinanceUsdPrice = async (symbol: string) => {
  const url = `https://api.binance.com/api/v3/ticker/price`;
  const { price } = await ofetch<{ price: number }>(url, {
    timeout: 2_000,
    query: { symbol: `${symbol}USDT` },
  });
  return Number(price);
};


/**
 * Get the USD price of a token.
 * @note Will try to get the price from the following sources in order:
 * - Binance
 * - Relay Link
 * - Chainlink
 * - If all fail, throw an error.
 * @param symbol
 * @param format
 */
export async function getUsdPrice(symbol: string, format: "bigint"): Promise<bigint>;
export async function getUsdPrice(symbol: string, format: "decimal"): Promise<number>;
export async function getUsdPrice(
  symbol: string,
  format: "bigint" | "decimal",
): Promise<bigint | number> {
  if (symbol === "USDC" || symbol === "USDT") {
    return format === "bigint"
      ? normalizePrice(1, "bigint", 8)
      : normalizePrice(1, "decimal", 8);
  }

  const price = await (async () => {

    const [binancePrice] = await catchBlock(() => getBinanceUsdPrice(symbol));
    if (binancePrice) return binancePrice;

    const [relayPrice] = await catchBlock(() => getRelayUsdPrice(symbol));
    if (relayPrice) return relayPrice;

    const [onChainPrice] = await catchBlock(() => getOnChainUsdPrice(symbol));
    if (onChainPrice) return onChainPrice;

    throw new Error("Failed to get USD price for symbol: " + symbol);
  })();

  if (format === "bigint") return normalizePrice(price, "bigint", 8);
  return normalizePrice(price, "decimal", 8);
}



/**
 * Convert a USD amount to a native amount.
 * @example usdToNative(100, "ETHEREUM") => 0.05504656 ETH
 * @param usdDecimalAmount
 * @param chainTag
 * @param usdPrice Optional USD price (`number` decimal or 8-decimal `bigint`). When provided, returns synchronously.
 */
export function usdToNative(
  usdDecimalAmount: number,
  chainTag: EvmChainTag,
  usdPrice: number | bigint,
): bigint;
export function usdToNative(
  usdDecimalAmount: number,
  chainTag: EvmChainTag,
): Promise<bigint>;
export function usdToNative(
  usdDecimalAmount: number,
  chainTag: EvmChainTag,
  usdPrice?: number | bigint,
): bigint | Promise<bigint> {
  if (usdPrice !== undefined) {
    const price = normalizePrice(usdPrice, "decimal", 8);
    return parseDenom(usdDecimalAmount / price, chainTag);
  }
  return (async () => {
    const chain = getChain(chainTag);
    const price = await getUsdPrice(chain.currency.symbol, "decimal");
    return parseDenom(usdDecimalAmount / price, chainTag);
  })();
}


/**
 * Convert a native amount to a USD amount.
 * @example nativeToUsd(1000000000000000000n, "ETHEREUM") => 100 USD
 * @param nativeAmount
 * @param chainTag
 * @param usdPrice Optional USD price (`number` decimal or 8-decimal `bigint`). When provided, returns synchronously.
 */
export function nativeToUsd(
  nativeAmount: bigint,
  chainTag: EvmChainTag,
  usdPrice: number | bigint,
): number;
export function nativeToUsd(
  nativeAmount: bigint,
  chainTag: EvmChainTag,
): Promise<number>;
export function nativeToUsd(
  nativeAmount: bigint,
  chainTag: EvmChainTag,
  usdPrice?: number | bigint,
): number | Promise<number> {
  if (usdPrice !== undefined) {
    const price = normalizePrice(usdPrice, "decimal", 8);
    return formatDenom(nativeAmount, chainTag) * price;
  }
  return (async () => {
    const chain = getChain(chainTag);
    const price = await getUsdPrice(chain.currency.symbol, "decimal");
    return formatDenom(nativeAmount, chainTag) * price;
  })();
}
